import { z } from "zod"
import {
  CorrespondenceEntityType,
  CorrespondenceThread,
} from "./correspondence"

export const CORRESPONDENCE_TYPES = z.enum(
  [
    "project_project_coordinator_organization_admins",
    "group_teacher_team",
    "student_teacher_team",
    "group_planner_client_manager",
    "student_planner_client_manager",
    "group_teacher_students",
    "student_teacher_student",
    "student_planner_student",
  ],
  { message: "Ongeldig correspondence type" },
)

export type CorrespondenceType = z.infer<typeof CORRESPONDENCE_TYPES>

export interface CorrespondenceEntry {
  type: CorrespondenceType
  entity: CorrespondenceEntityType
  entityLabel: string

  showVisibleForTeachers?: boolean
  showVisibleForOrganizationUsers?: boolean
}

export interface CorrespondenceGroup {
  label: string
  types: CorrespondenceEntry[]
}

// Top-level structuur voor permissions
export interface CorrespondencePermissions {
  [root: string]: {
    [role: string]: CorrespondenceGroup[]
  }
}

export type ConstrainBooleanParameters<T = unknown> = T

export const CORRESPONDENCE_PERMISSIONS: ConstrainBooleanParameters<CorrespondencePermissions> =
  {
    mtt: {
      project_coordinator: [
        {
          label: "Organisatie admins",
          types: [
            {
              type: "project_project_coordinator_organization_admins",
              entity: "Project",
              entityLabel: "Project",
            },
          ],
        },
        {
          label: "Docent",
          types: [
            {
              type: "group_teacher_team",
              showVisibleForOrganizationUsers: true,
              entity: "LessonGroup",
              entityLabel: "Groep",
            },
            {
              type: "student_teacher_team",
              showVisibleForOrganizationUsers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
      ],

      executive_employee: [
        {
          label: "Docent",
          types: [
            {
              type: "group_teacher_team",
              showVisibleForOrganizationUsers: true,
              entity: "LessonGroup",
              entityLabel: "Groep",
            },
            {
              type: "student_teacher_team",
              showVisibleForOrganizationUsers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
        {
          label: "Klantmanager",
          types: [
            {
              type: "group_planner_client_manager",
              showVisibleForTeachers: true,
              entity: "LessonGroup",
              entityLabel: "Groep",
            },
            {
              type: "student_planner_client_manager",
              showVisibleForTeachers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
        {
          label: "Student",
          types: [
            {
              type: "student_planner_student",
              showVisibleForOrganizationUsers: true,
              showVisibleForTeachers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
      ],

      teacher: [
        {
          label: "TopTaal Team",
          types: [
            {
              type: "group_teacher_team",
              showVisibleForOrganizationUsers: true,
              entity: "LessonGroup",
              entityLabel: "Groep",
            },
            {
              type: "student_teacher_team",
              showVisibleForOrganizationUsers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
        {
          label: "Student",
          types: [
            {
              type: "student_teacher_student",
              showVisibleForOrganizationUsers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
        {
          label: "Groepsapp studenten",
          types: [
            {
              type: "group_teacher_students",
              showVisibleForOrganizationUsers: true,
              entity: "LessonGroup",
              entityLabel: "Groep",
            },
          ],
        },
      ],
    },

    org: {
      admin: [
        {
          label: "Project coördinator",
          types: [
            {
              type: "project_project_coordinator_organization_admins",
              entity: "Project",
              entityLabel: "Project",
            },
          ],
        },
      ],

      contact_manager: [
        {
          label: "Planners",
          types: [
            {
              type: "group_planner_client_manager",
              showVisibleForTeachers: true,
              entity: "LessonGroup",
              entityLabel: "Groep",
            },
            {
              type: "student_planner_client_manager",
              showVisibleForTeachers: true,
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
      ],
    },

    student: {
      student: [
        {
          label: "Planners",
          types: [
            {
              type: "student_planner_student",
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
        {
          label: "Docenten",
          types: [
            {
              type: "student_teacher_student",
              entity: "Student",
              entityLabel: "Student",
            },
          ],
        },
      ],
    },
  }

export function filterVisibleTypesBasedOnCorrespondenceType(
  types: CorrespondenceType | CorrespondenceType[],
): Pick<
  CorrespondenceThread,
  "visibleForOrganizationUsers" | "visibleForTeachers"
> {
  const typeSet = new Set(Array.isArray(types) ? types : [types])

  let showVisibleForOrganizationUsers = false
  let showVisibleForTeachers = false

  const mttPermissions = CORRESPONDENCE_PERMISSIONS.mtt

  Object.values(mttPermissions).forEach((groups) => {
    groups.forEach((group) => {
      group.types.forEach((entry) => {
        if (!typeSet.has(entry.type)) return

        if (entry.showVisibleForOrganizationUsers) {
          showVisibleForOrganizationUsers = true
        }

        if (entry.showVisibleForTeachers) {
          showVisibleForTeachers = true
        }
      })
    })
  })

  return {
    visibleForOrganizationUsers: showVisibleForOrganizationUsers,
    visibleForTeachers: showVisibleForTeachers,
  }
}
